import React, { useState, useCallback, useEffect } from 'react';
import { useDropzone } from 'react-dropzone';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { CloudArrowUpIcon, DocumentTextIcon, CheckCircleIcon, XCircleIcon, ExclamationTriangleIcon, TrashIcon } from '@heroicons/react/24/outline';

const CSVUpload = () => {
  const [isUserAuthorized, setIsUserAuthorized] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [flushing, setFlushing] = useState(false);
  const [uploadResult, setUploadResult] = useState(null);
  const [flushResult, setFlushResult] = useState(null);
  const [error, setError] = useState('');
  const [showFlushConfirm, setShowFlushConfirm] = useState(false);
  const { isAuthenticated, user, userRole } = useAuth();

  const addUpdateAuditLog = useCallback((records_processed) => {
    try {
      // Add an audit log that the current user has updated employee records
      const auditLogData = {
        action: 'update',
        employee_id: null,
        changes: `Processed ${records_processed} records`,
        table_name: 'employees',
        performed_by: user
      };

      axios.post(
        `${process.env.REACT_APP_API_URL}/add-audit-log`,
        auditLogData,
        {
          withCredentials: true
        }
      );
    } catch (err) {
      setError('Failed to create audit log');
      console.error('Error creating audit log:', err);
    }
  }, [user]);

  useEffect(() => {
    // Check if the user is authorized to upload data
    const checkIfUserIsAuthorized = async () => {
      const doesUserHavePermissions = userRole === "super_admin_user_role";
      setIsUserAuthorized(doesUserHavePermissions);
    };

    if (isAuthenticated) {
      checkIfUserIsAuthorized();
    }
  }, [userRole, isAuthenticated]);

  const onDrop = useCallback(async (acceptedFiles) => {
    if (!isAuthenticated) {
      setError('Please log in to upload files');
      return;
    }
    
    if (acceptedFiles.length === 0) return;

    const file = acceptedFiles[0];
    
    // Validate file type
    if (!file.name.endsWith('.csv')) {
      setError('Please upload a CSV file');
      return;
    }

    setUploading(true);
    setError('');
    setUploadResult(null);
    setFlushResult(null);

    const formData = new FormData();
    formData.append('file', file);

    try {
      const response = await axios.post(
        `${process.env.REACT_APP_API_URL}/upload/csv`,
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data'
          },
          withCredentials: true
        }
      );

      setUploadResult(response.data);
      addUpdateAuditLog(response.data.records_processed);
    } catch (err) {
      setError(err.response?.data?.detail || 'Upload failed');
      console.error('Upload error:', err);
    } finally {
      setUploading(false);
    }
  }, [isAuthenticated, addUpdateAuditLog]);

  const handleFlushDatabase = async () => {
    if (!isAuthenticated) {
      setError('Please log in to flush database');
      return;
    }

    setFlushing(true);
    setError('');
    setUploadResult(null);
    setFlushResult(null);
    setShowFlushConfirm(false);

    try {
      const response = await axios.post(
        `${process.env.REACT_APP_API_URL}/admin/flush-database`,
        {},
        {
          withCredentials: true
        }
      );

      setFlushResult(response.data);
    } catch (err) {
      setError(err.response?.data?.detail || 'Database flush failed');
      console.error('Flush error:', err);
    } finally {
      setFlushing(false);
    }
  };

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'text/csv': ['.csv'],
    },
    multiple: false,
  });

  return (
    <div className="space-y-6">
      {isUserAuthorized ? (
        <>
          {/* Development Warning */}
          <div className="bg-orange-50 border-l-4 border-orange-400 p-4">
            <div className="flex">
              <ExclamationTriangleIcon className="h-5 w-5 text-orange-400" />
              <div className="ml-3">
                <p className="text-sm font-medium text-orange-800">
                  THIS PAGE IS FOR DEVELOPMENT PURPOSES
                </p>
                <p className="mt-1 text-sm text-orange-700">
                  Use with caution. Database operations performed here will affect all data in the system.
                </p>
              </div>
            </div>
          </div>

          {/* Header */}
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Upload CSV</h1>
            <p className="mt-1 text-sm text-gray-500">
              Upload employee data from a CSV file. The file will be processed and normalized into the database with comprehensive timeline tracking.
            </p>
          </div>

          {/* Flush Database Section */}
          <div className="bg-red-50 border border-red-200 rounded-lg">
            <div className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-lg font-medium text-red-900">Flush Database</h3>
                  <p className="mt-1 text-sm text-red-700">
                    Clear all existing data before uploading new CSV. This action cannot be undone.
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() => setShowFlushConfirm(true)}
                  disabled={flushing || uploading}
                  className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {flushing ? (
                    <>
                      <div className="spinner-sm mr-2"></div>
                      Flushing...
                    </>
                  ) : (
                    <>
                      <TrashIcon className="h-4 w-4 mr-2" />
                      Flush Database
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>

          {/* Confirmation Modal */}
          {showFlushConfirm && (
            <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
              <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
                <div className="mt-3 text-center">
                  <ExclamationTriangleIcon className="mx-auto h-12 w-12 text-red-400" />
                  <h3 className="text-lg font-medium text-gray-900 mt-4">
                    Confirm Database Flush
                  </h3>
                  <div className="mt-2 px-7 py-3">
                    <p className="text-sm text-gray-500">
                      This will permanently delete ALL employee data, including:
                    </p>
                    <ul className="text-xs text-gray-600 mt-2 text-left list-disc list-inside space-y-1">
                      <li>All employee records</li>
                      <li>Career history and timelines</li>
                      <li>Salary history</li>
                      <li>Leave records</li>
                      <li>Employment status history</li>
                      <li>Personal details</li>
                    </ul>
                    <p className="text-sm text-red-600 mt-3 font-medium">
                      This action cannot be undone!
                    </p>
                  </div>
                  <div className="items-center px-4 py-3">
                    <button
                      onClick={handleFlushDatabase}
                      className="px-4 py-2 bg-red-600 text-white text-base font-medium rounded-md w-full shadow-sm hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-300 mr-2"
                    >
                      Yes, Flush Database
                    </button>
                    <button
                      onClick={() => setShowFlushConfirm(false)}
                      className="mt-3 px-4 py-2 bg-gray-300 text-gray-800 text-base font-medium rounded-md w-full shadow-sm hover:bg-gray-400 focus:outline-none focus:ring-2 focus:ring-gray-300"
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Upload Area */}
          <div className="bg-white shadow rounded-lg">
            <div className="p-6">
              <div
                {...getRootProps()}
                className={`border-2 border-dashed rounded-lg p-12 text-center cursor-pointer transition-colors ${
                  isDragActive
                    ? 'border-primary-400 bg-primary-50'
                    : 'border-gray-300 hover:border-primary-400 hover:bg-gray-50'
                }`}
              >
                <input {...getInputProps()} />
                
                {uploading ? (
                  <div className="space-y-4">
                    <div className="spinner mx-auto"></div>
                    <p className="text-sm text-gray-600">Processing CSV file...</p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    <CloudArrowUpIcon className="mx-auto h-12 w-12 text-gray-400" />
                    <div className="text-sm text-gray-600">
                      <p className="font-medium">
                        {isDragActive ? 'Drop the CSV file here' : 'Drag and drop a CSV file here'}
                      </p>
                      <p className="mt-1">or click to browse</p>
                    </div>
                    <p className="text-xs text-gray-500">
                      Supports .csv files only
                    </p>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Error Message */}
          {error && (
            <div className="bg-red-50 border border-red-200 rounded-md p-4">
              <div className="flex">
                <XCircleIcon className="h-5 w-5 text-red-400" />
                <div className="ml-3">
                  <h3 className="text-sm font-medium text-red-800">Error</h3>
                  <div className="mt-2 text-sm text-red-700">{error}</div>
                </div>
              </div>
            </div>
          )}

          {/* Flush Success Message */}
          {flushResult && (
            <div className="bg-green-50 border border-green-200 rounded-md p-4">
              <div className="flex">
                <CheckCircleIcon className="h-5 w-5 text-green-400" />
                <div className="ml-3">
                  <h3 className="text-sm font-medium text-green-800">Database Flushed Successfully</h3>
                  <div className="mt-2 text-sm text-green-700">
                    <p>Records deleted: {flushResult.records_deleted}</p>
                    <p>Tables cleared: {flushResult.tables_cleared}</p>
                    <p>Database is now ready for fresh data upload.</p>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Upload Success Message */}
          {uploadResult && (
            <div className="bg-green-50 border border-green-200 rounded-md p-4">
              <div className="flex">
                <CheckCircleIcon className="h-5 w-5 text-green-400" />
                <div className="ml-3">
                  <h3 className="text-sm font-medium text-green-800">Upload Successful</h3>
                  <div className="mt-2 text-sm text-green-700">
                    <p>Records processed: {uploadResult.records_processed}</p>
                    <p>Employees added: {uploadResult.employees_added}</p>
                    <p>Positions added: {uploadResult.positions_added}</p>
                    <p>Locations added: {uploadResult.locations_added}</p>
                    {uploadResult.status_records_added > 0 && (
                      <p>Status records added: {uploadResult.status_records_added}</p>
                    )}
                    {uploadResult.leave_records_added > 0 && (
                      <p>Leave records added: {uploadResult.leave_records_added}</p>
                    )}
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Instructions */}
          <div className="bg-blue-50 border border-blue-200 rounded-md p-4">
            <div className="flex">
              <DocumentTextIcon className="h-5 w-5 text-blue-400" />
              <div className="ml-3">
                <h3 className="text-sm font-medium text-blue-800">Enhanced CSV Format Requirements</h3>
                <div className="mt-2 text-sm text-blue-700">
                  <p>Your CSV file should include the following columns for comprehensive timeline tracking:</p>
                  
                  <div className="mt-3 grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <h4 className="font-medium text-blue-800 mb-2">Required Core Fields:</h4>
                      <ul className="list-disc list-inside space-y-1 text-xs">
                        <li><strong>employee_id</strong> - Unique employee identifier</li>
                        <li><strong>name</strong> - Employee full name</li>
                        <li><strong>hire_date</strong> - Date of hire (YYYY-MM-DD)</li>
                        <li><strong>position</strong> - Job title</li>
                        <li><strong>department</strong> - Department name</li>
                        <li><strong>location</strong> - Office location</li>
                        <li><strong>salary</strong> - Annual salary amount</li>
                        <li><strong>effective_date</strong> - When this record takes effect</li>
                      </ul>
                    </div>
                    
                    <div>
                      <h4 className="font-medium text-blue-800 mb-2">Optional Enhanced Fields:</h4>
                      <ul className="list-disc list-inside space-y-1 text-xs">
                        <li><strong>employment_status</strong> - active, inactive, on_leave, terminated, retired, suspended, probation</li>
                        <li><strong>status_reason</strong> - Reason for status change</li>
                        <li><strong>leave_type</strong> - vacation, sick_leave, maternity_leave, medical_leave, workers_comp, etc.</li>
                        <li><strong>leave_start_date</strong> - When leave begins</li>
                        <li><strong>leave_end_date</strong> - When leave ends</li>
                        <li><strong>workers_comp_case_number</strong> - For workers compensation cases</li>
                        <li><strong>disability_claim_number</strong> - For disability claims</li>
                        <li><strong>medical_documentation</strong> - True/False for medical documentation</li>
                      </ul>
                    </div>
                  </div>
                  
                  <div className="mt-3 p-3 bg-blue-100 rounded">
                    <p className="text-xs font-medium text-blue-800 mb-1">💡 Timeline Enhancement:</p>
                    <p className="text-xs text-blue-700">
                      Including employment status and leave records creates a comprehensive timeline that tracks active/inactive periods, 
                      breaks, holidays, and legal/medical leave - perfect for compliance and legal cases.
                    </p>
                  </div>
                  
                  <p className="mt-3">
                    <a
                      href="/sample_data/employees_sample.csv"
                      download
                      className="text-blue-600 hover:text-blue-500 underline"
                    >
                      Download enhanced sample CSV template
                    </a>
                  </p>
                </div>
              </div>
            </div>
          </div>
        </>
      ) : (
        <>
          {/* Header */}
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Upload CSV</h1>
            <p className="mt-1 text-sm text-gray-500">
              Please contact your administrator to upgrade your permissions to super admin if you would like to upload employee data from a CSV file.
            </p>
          </div>
        </>
      )}
    </div>
  );
};

export default CSVUpload; 
